import React from 'react';
import type { ReactElement } from 'react';
import styled, { css } from 'styled-components';

import SearchInput from '@zimpel/common/components/SearchInput';
import { UnstyledList } from '@zimpel/common/components/Lists';

const StyledAutocomplete = styled.div`
  position: relative;
  width: 100%;
`;

const MenuWrapper = styled.div<{ $isBorderlessStyle?: boolean }>`
  position: absolute;
  width: calc(100% - 2px);
  background-color: var(--color-input-background);
  color: var(--color-text-default);
  border: var(--border-input);
  z-index: var(--z-index-autocomplete-menu);
  ${({ $isBorderlessStyle }) => ($isBorderlessStyle
    ? css`
      width: calc(100% + (2 * var(--border-box-horizontal-padding)));
      margin-left: calc(-1 * (var(--border-box-horizontal-padding) + 1px));
    `
    : css`
      margin-top: -2px;
    `
  )}
`;

const Menu = styled(UnstyledList) <{ $maxHeightInPx: number }>`
  overflow: auto;
  max-height: ${({ $maxHeightInPx }) => `${$maxHeightInPx}px`};
`;

const MenuItem = styled.li<{ $isSelectable: boolean, $isHighlighted: boolean }>`
  ${({ $isSelectable }) => $isSelectable && css`
    cursor: pointer;
  `}
  ${({ $isHighlighted }) => $isHighlighted && css`
    background-color: var(--color-background-bright-hover);
  `}
`;

type Item = {
  element: ReactElement;
  onSelect?: () => void;
  ref?: HTMLLIElement | null;
};

type Props = {
  items: Item[];
  inputValue: string;
  inputId?: string;
  isOpen: boolean;
  isItemHighlighted: (idx: number) => boolean;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onInputChange: (value: string) => Promise<void>;
  onCancel: () => void;
  onSelectItem: (item: Item, shiftKey: boolean) => void;
  onHighlightItem: (idx: number) => void;
  placeholder?: string;
  autoFocus?: boolean;
  $isBorderlessStyle?: boolean;
  hasErrors?: boolean;
  disabled?: boolean;
  maxHeight?: number;
  isLoading?: boolean;
  isTestingMode?: boolean;
};

const AutoComplete: React.FC<Props> = ({
  items,
  inputValue,
  inputId,
  isOpen,
  isItemHighlighted,
  onFocus,
  onBlur,
  onKeyDown,
  onInputChange,
  onCancel,
  onSelectItem,
  onHighlightItem,
  autoFocus,
  placeholder,
  $isBorderlessStyle = false,
  hasErrors = false,
  disabled = false,
  maxHeight = 224,
  isLoading = false,
  isTestingMode = false,
}) => (
  <StyledAutocomplete>
    <SearchInput
      $isBorderlessStyle={$isBorderlessStyle}
      inputId={inputId}
      placeholder={placeholder}
      value={inputValue}
      isLoading={isLoading}
      onCancel={onCancel}
      hasError={hasErrors}
      disabled={disabled}
      autoFocus={autoFocus}
      onKeyDown={onKeyDown}
      onValueChange={(value) => onInputChange(value)}
      onFocus={onFocus}
      onBlur={onBlur}
    />
    {isOpen && items.length > 0 && (
      <MenuWrapper $isBorderlessStyle={$isBorderlessStyle}>
        <Menu data-test="autocomplete-menu" $maxHeightInPx={maxHeight}>
          {items.map((item, idx) => (
            <MenuItem
              $isSelectable={!!item.onSelect}
              $isHighlighted={isItemHighlighted(idx)}
              data-test="autocomplete-item"
              key={idx}
              ref={(ref): void => {
                if (!isTestingMode) {
                  // This would cause a memory leak in story book
                  item.ref = ref;
                }
              }}
              onMouseDown={(event): void => {
                // We handle the onMouseDown event instead of the onClick
                // event to avoid losing the focus on the input element.
                event.preventDefault();
                onSelectItem(item, event.shiftKey);
              }}
              onMouseOver={() => { onHighlightItem(idx); }}
              onFocus={() => { /* onFocus required by eslint */ }}
            >
              {item.element}
            </MenuItem>
          ))}
        </Menu>
      </MenuWrapper>
    )}
  </StyledAutocomplete>
);

export { AutoComplete };
export type { Props, Item };
